import { FC, useEffect } from "react";
import { FieldFormV2, FieldV2, LoadingV2 } from "@ucl/library";
import { useFinancialApplicationForm } from "@/hooks/useFinancialApplicationForm";
import { FIELD_KEYS } from "@/constants";
import "./BasicInformationForm.scss";
import { FinancialApplication } from "@/types/FinancialApplication";
import { useFinancialApplicationCoreData } from "@/hooks/useFinancialApplicationCoreData";
import { PersonInformationComplexValue } from "../ComplexFields/PersonInformationComplexField";
import {
  useFinancialApplicationFormSectionValidation,
  FinancialApplicationFormValidationDisplayBehavior,
  FinancialApplicationFormSectionContextType,
} from "@/hooks/useFinancialApplicationFormSectionValidation";
import { FinancialApplicationSection } from "@/types/enums";
import { GovernmentIdInformationComplexValue } from "../ComplexFields/GovernmentIdInformationComplexField";
import { EmploymentInformationComplexValue } from "../ComplexFields/EmploymentInformationComplexField";
import { DigitalBankingInformationComplexFieldValue } from "../ComplexFields/DigitalBankingInformationComplexField";
import { SecurityQuestionsInformationComplexValue } from "../ComplexFields/SecurityQuestionsInformationComplexField";
import { DebitCardInformationComplexValue } from "../ComplexFields/DebitCardInformationComplexField";

export interface BasicInformationFormProps {
  financialApplicationId: string;
  autoValidationBehavior?: FinancialApplicationFormValidationDisplayBehavior;
  availableFields?: string[];
  showHeader?: boolean;
  validationContextType: FinancialApplicationFormSectionContextType;
  shouldShowDigitalBankingFields?: boolean;
  shouldShowDebitCardFields?: boolean;
}

export const BasicInformationForm: FC<BasicInformationFormProps> = (props) => {
  const {
    formValue,
    setFormValue,
    saveFormData,
    schemaFactory,
    formBuilder,
    fieldFormModule,
    enqueueAfterNextSave,
  } = useFinancialApplicationForm(props.financialApplicationId, props.validationContextType);

  const { validationErrors } = useFinancialApplicationFormSectionValidation(
    props.financialApplicationId,
    FinancialApplicationSection.PersonInformationBasicInfo,
    props.autoValidationBehavior,
    props.validationContextType
  );

  const { conditionalFlags, refreshCoreData } = useFinancialApplicationCoreData(
    props.financialApplicationId
  );

  // Refetch core data when these fields change to update conditional flags
  useEffect(() => {
    if (formValue) {
      enqueueAfterNextSave(async () => {
        await refreshCoreData();
      });
    }
  }, [formValue?.willHaveJointAccountOwners, formValue?.willHavePersonalAccountBeneficiaries]);

  if (!schemaFactory || !formBuilder || !formValue) {
    return (
      <section className="basic-information-form">
        <LoadingV2 />
      </section>
    );
  }

  return (
    <div className="basic-information-form">
      <FieldFormV2<FinancialApplication>
        ref={fieldFormModule}
        value={formValue}
        fieldSchemaFormBuilder={formBuilder}
        onFormSubmit={async (value) => {
          await saveFormData(value);
        }}
        onFieldChanged={(newValue, newFieldValue, _fieldProps) => {
          setFormValue(newValue, newFieldValue);
        }}
        errors={validationErrors}
        availableFields={props.availableFields}
      >
        <FieldV2<PersonInformationComplexValue>
          fieldProps={FIELD_KEYS.FINANCIAL_APPLICATION.PERSON_INFORMATION}
          complexFieldValue={{
            showHeader: props.showHeader,
            headerLabel: "Personal Information",
            hiddenFields: ["PHONE_NUMBER", "EMAIL_ADDRESS", "HOME_ADDRESS"],
            className: "personal-information",
          }}
        />

        <FieldV2<GovernmentIdInformationComplexValue>
          fieldProps={FIELD_KEYS.FINANCIAL_APPLICATION.GOVERNMENT_ID_INFORMATION}
          complexFieldValue={{
            showHeader: props.showHeader,
            headerLabel: "Identification Information",
          }}
        />

        <FieldV2<PersonInformationComplexValue>
          fieldProps={FIELD_KEYS.FINANCIAL_APPLICATION.PERSON_INFORMATION}
          complexFieldValue={{
            showHeader: props.showHeader,
            headerLabel: "Contact Information",
            hiddenFields: [
              "FIRST_NAME",
              "MIDDLE_NAME",
              "LAST_NAME",
              "DATE_OF_BIRTH",
              "SOCIAL_SECURITY_NUMBER",
            ],
            className: "contact-information",
          }}
        />

        <FieldV2<EmploymentInformationComplexValue>
          fieldProps={FIELD_KEYS.FINANCIAL_APPLICATION.EMPLOYMENT_INFORMATION}
          complexFieldValue={{
            showHeader: props.showHeader,
            headerLabel: "Employment Information",
            showAttorneyBarNumber: conditionalFlags.shouldShowAttorneyBarNumber,
          }}
        />

        <FieldV2<DigitalBankingInformationComplexFieldValue>
          fieldProps={FIELD_KEYS.FINANCIAL_APPLICATION.DIGITAL_BANKING_INFORMATION}
          complexFieldValue={{
            isAccountOpenerForm: true,
            shouldShowDigitalBankingFields: props.shouldShowDigitalBankingFields,
            showHeader: props.showHeader,
            headerLabel: "Digital Banking",
          }}
        />

        <FieldV2<SecurityQuestionsInformationComplexValue>
          fieldProps={FIELD_KEYS.FINANCIAL_APPLICATION.SECURITY_QUESTIONS_INFORMATION}
          complexFieldValue={{
            showHeader: false,
          }}
        />

        <FieldV2<DebitCardInformationComplexValue>
          fieldProps={FIELD_KEYS.FINANCIAL_APPLICATION.DEBIT_CARD_INFORMATION}
          complexFieldValue={{
            showHeader: props.showHeader,
            headerLabel: "Debit Card",
            shouldShowBusinessName: conditionalFlags.showBusinessCard,
            shouldShowDebitCardFields: props.shouldShowDebitCardFields,
          }}
        />
      </FieldFormV2>
    </div>
  );
};
